import { z } from "zod";

enum StateCodes {
  Johor = "01",
  Kedah = "02",
  Kelantan = "03",
  Melaka = "04",
  NegeriSembilan = "05",
  Pahang = "06",
  PulauPinang = "07",
  Perak = "08",
  Perlis = "09",
  Selangor = "10",
  Terengganu = "11",
  Sabah = "12",
  Sarawak = "13",
  WilayahPersekutuanKualaLumpur = "14",
  WilayahPersekutuanLabuan = "15",
  WilayahPersekutuanPutrajaya = "16",
  NotApplicable = "17",
}

export const selfServiceSchema = z.object({
  // businessBrn: z
  //   .string()
  //   .min(1, { message: "Business BRN is required" }),
  businessId: z.string().min(1),
  branchId: z.string().min(1),
  postReferenceNumber: z
    .string({ message: "Post reference number is required" })
    .min(1, { message: "Post reference number is required" })
    .max(50, { message: "Maximum invoice number entered" }),
  invoiceDate: z
    .string({ message: "Invoice date is required" })
    .min(1, { message: "Invoice date is required" }),
  totalPayableAmount: z
    .number({ message: "Invoice total amount is required" })
    .gte(1, { message: "Invoice total amount is required" })
    .nonnegative({ message: "Negative numbers are not allowed" }),
});

export const selfServiceBuyerDetails = z.object({
  buyerRegistrationType: z
    .string()
    .min(1, { message: "Registration Type is required" })
    .max(20, {
      message: "Registration Type must be at most 20 characters long",
    }),
  buyerRegistrationNumber: z
    .string()
    .min(1, { message: "Registration Number is required" })
    .max(20, {
      message: "Registration Number must be at most 20 characters long",
    }),
  buyerTin: z
    .string()
    .min(1, { message: "TIN is required" })
    .max(14, { message: "TIN must be at most 14 characters long" }),
  buyerName: z
    .string()
    .min(1, { message: "Name is required" })
    .max(300, { message: "Name must be at most 300 characters long" }),
  buyerSstRegistrationNumber: z
    .string()
    .max(17, {
      message: "SST Registration Number must be at most 17 characters long",
    }),
  buyerEmail: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email address" })
    .max(320, { message: "Email must be at most 320 characters long" }),
  buyerContact: z.string()
    .min(8, { message: "Contact number must be at least 8 characters" })
    .max(20, { message: "Contact number must not exceed 20 characters" })
    .regex(/^\+?\d+$/, { message: "Contact number must contain only digits with an optional plus (+) symbol at the start and no spaces" }),
  buyerAddressLine0: z
    .string()
    .min(1, { message: "Address 1 is required" })
    .max(40, {
      message: "Exceed maximum number of characters allowed.",
    }),
  buyerAddressLine1: z.string().nullable(),
  buyerAddressLine2: z.string().nullable(),
  buyerCity: z.string().min(1, { message: "City is required" }).max(20, {
    message: "City must be at most 20 characters long",
  }),
  buyerPostcode: z.string().optional(),
  buyerState: z.nativeEnum(StateCodes),
  buyerCountry: z.string().length(3),
  isConsolidated: z.boolean().optional(),
  status: z.string().nullish(),
});

export const lineItemSchema = z.object({
  id: z.string(),
  classification: z.string(),
  description: z.string(),
  unitPrice: z.string(),
  taxType: z.string(),
  taxRate: z.string(),
  taxAmount: z.string(),
  subtotal: z.string(),
  totalExcludingTax: z.string(),
  quantity: z.string(),
  discountRate: z.string(),
  discountAmount: z.string(),
  feeChargeRate: z.string(),
  feeChargeAmount: z.string(),
  measurement: z.string().nullable(),
  itemCode: z.string(),
  amountExemptedFromTax: z.number().nullable(),
  detailsOfTaxExemption: z.string().nullable(),
  productTariffCode: z.string().nullable(),
  countryOfOrigin: z.string().nullable(),
});
export type LineItem = z.infer<typeof lineItemSchema>;

export const selfServiceInvoiceSchema = z.object({
  id: z.string(),
  version: z.string(),
  typeCode: z.string(),
  codeNumber: z.string(),
  invoiceDate: z.coerce.date(),
  invoiceTime: z.coerce.date(),
  issuerDigitalSignature: z.string(),
  currencyCode: z.string(),
  currencyExchangeRate: z.string(),
  frequencyOfBilling: z.string().nullable(),
  billingPeriodStartDate: z.string().nullable(),
  billingPeriodEndDate: z.string().nullable(),
  paymentMode: z.string().nullable(),
  supplierBankAccountNumber: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  prepaymentAmount: z.string().nullable(),
  prepaymentDate: z.string().nullable(),
  prepaymentTime: z.string().nullable(),
  prepaymentReferenceNumber: z.string().nullable(),
  billReferenceNumber: z.string().nullable(),
  totalExcludingTax: z.string(),
  totalIncludingTax: z.string(),
  totalPayableAmount: z.string(),
  totalNetAmount: z.string().nullable(),
  totalDiscountValue: z.string().nullable(),
  totalFeeChargeAmount: z.string().nullable(),
  totalTaxAmount: z.string(),
  roundingAmount: z.string().nullable(),
  totalTaxableAmountPerTaxType: z.string().nullable(),
  totalTaxAmountPerTaxType: z.string(),
  taxType: z.string(),
  invoiceAdditionalDiscountAmount: z.string().nullable(),
  invoiceAdditionalFeeAmount: z.string().nullable(),
  originalInvoiceNumber: z.string().nullable(),
  reason: z.string().nullable(),
  originalInvoiceReference: z.string().nullable(),

  buyerRegistrationType: z
    .string()
    .min(1, { message: "Registration Type is required" })
    .max(20, {
      message: "Registration Type must be at most 20 characters long",
    }),
  buyerRegistrationNumber: z
    .string()
    .min(1, { message: "Registration Number is required" })
    .max(20, {
      message: "Registration Number must be at most 20 characters long",
    }),
  buyerTin: z
    .string()
    .min(1, { message: "TIN is required" })
    .max(14, { message: "TIN must be at most 14 characters long" }),
  buyerName: z
    .string()
    .min(1, { message: "Name is required" })
    .max(300, { message: "Name must be at most 300 characters long" }),
  buyerSstRegistrationNumber: z
    .string()
    .max(17, {
      message: "SST Registration Number must be at most 17 characters long",
    }),
  buyerEmail: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email address" })
    .max(320, { message: "Email must be at most 320 characters long" }),
  buyerContact: z.string()
  .min(1, { message: "Contact number is required" })
  .refine((val) => {
    if (val === "NA") return true;
    return val.length >= 8 && val.length <= 20 && /^\+?\d+$/.test(val);
  }, {
    message: "Contact number must contain only digits with an optional plus (+) symbol at the start and no spaces"
  }),
  buyerAddressLine0: z
    .string()
    .min(1, { message: "Address 1 is required" })
    .max(40, {
      message: "Exceed maximum number of characters allowed.",
    }),
  buyerAddressLine1: z.string().nullable(),
  buyerAddressLine2: z.string().nullable(),
  buyerCity: z.string().min(1, { message: "City is required" }).max(20, {
    message: "City must be at most 20 characters long",
  }),
  buyerPostcode: z.string().optional(),
  buyerState: z.nativeEnum(StateCodes),
  buyerCountry: z.string().length(3),

  supplierRegistrationType: z.string(),
  supplierRegistrationNumber: z.string(),
  supplierTin: z.string(),
  supplierName: z.string(),
  supplierSstRegistrationNumber: z.string(),
  supplierMsicCode: z.string(),
  supplierTourismTaxRegistrationNumber: z.string(),
  supplierBusinessActivityDescription: z.string(),
  supplierEmail: z.string(),
  supplierContact: z.string(),
  supplierAddressLine0: z.string(),
  supplierAddressLine1: z.string().optional(),
  supplierAddressLine2: z.string().optional(),
  supplierCity: z.string(),
  supplierPostcode: z.string(),
  supplierState: z.string(),
  supplierCountry: z.string(),
  lineItems: z.array(lineItemSchema)
});

export const lineItemOcrSchema = z.object({
  id: z.string().optional(),
  classification: z.string(),
  description: z.string().max(300),
  unitPrice: z.number(),
  taxType: z.enum(["01", "02", "03", "04", "05", "06", "E"]),
  codeNumber: z.string(),
  taxRate: z.number(),
  taxAmount: z.number(),
  detailsOfTaxExemption: z.string().nullish().default(null), // Mandatory if tax exemption is applicable
  amountExemptedFromTax: z.number().nullish().default(null), // Mandatory if detailsOfTaxExemption is provided
  subtotal: z.number(),
  totalExcludingTax: z.number(),
  quantity: z.number(),
  measurement: z.string().max(3).nullish(),
  discountRate: z.number().nullable(),
  discountAmount: z.number().nullable(),
  feeRate: z.number().nullable(),
  feeAmount: z.number().nullable(),
  productTariffCode: z.string().max(12).optional().nullable(),
  countryOfOrigin: z.string().max(3).optional(),
  total: z.number(),
  _delete: z.boolean().optional()
});
export type LineItemOcr = z.infer<typeof lineItemOcrSchema>;

export const selfServiceOcrInvoiceSchema = z.object({
  invoiceDate: z.string(),
  invoiceTime: z.string(),
  issuerDigitalSignature: z.string(),
  // currencyCode: z.string(),
  currencyExchangeRate: z.number().nullable().default(null),
  frequencyOfBilling: z.string().nullable(),
  billingPeriodStartDate: z.string().nullable(),
  billingPeriodEndDate: z.string().nullable(),
  paymentMode: z.string().nullable(),
  supplierBankAccountNumber: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  prepaymentAmount: z.string().nullable(),
  prepaymentDate: z.string().nullable(),
  // prepaymentTime: z.string().nullable(),
  prepaymentReferenceNumber: z.string().nullable(),
  billReferenceNumber: z.string().nullable(),

  roundingAmount: z.number().nullable().default(null),
  invoiceAdditionalDiscountAmount: z.number().nullable().default(null),
  invoiceAdditionalFeeAmount: z.number().nullable().default(null),
  originalInvoiceNumber: z.string().nullable(),

  buyerRegistrationType: z
    .string()
    .min(1, { message: "Registration Type is required" })
    .max(20, {
      message: "Registration Type must be at most 20 characters long",
    }),
  buyerRegistrationNumber: z
    .string()
    .min(1, { message: "Registration Number is required" })
    .max(20, {
      message: "Registration Number must be at most 20 characters long",
    }),
  buyerTin: z
    .string()
    .min(1, { message: "TIN is required" })
    .max(14, { message: "TIN must be at most 14 characters long" }),
  buyerName: z
    .string()
    .min(1, { message: "Name is required" })
    .max(300, { message: "Name must be at most 300 characters long" }),
  buyerSstRegistrationNumber: z
    .string()
    .max(17, {
      message: "SST Registration Number must be at most 17 characters long",
    }),
  buyerEmail: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email address" })
    .max(320, { message: "Email must be at most 320 characters long" }),
  buyerContact: z.string()
  .min(1, { message: "Contact number is required" })
  .refine((val) => {
    if (val === "NA") return true;
    return val.length >= 8 && val.length <= 20 && /^\+?\d+$/.test(val);
  }, {
    message: "Contact number must contain only digits with an optional plus (+) symbol at the start and no spaces"
  }),
  buyerAddressLine0: z
    .string()
    .min(1, { message: "Address 1 is required" })
    .max(40, {
      message: "Exceed maximum number of characters allowed.",
    }),
  buyerAddressLine1: z.string().nullable(),
  buyerAddressLine2: z.string().nullable(),
  buyerCity: z.string().min(1, { message: "City is required" }).max(20, {
    message: "City must be at most 20 characters long",
  }),
  buyerPostcode: z.string().nullable(),
  buyerState: z
    .string({ message: "State is required" })
    .min(1, { message: "State is required" }),
  buyerCountry: z.string().length(3),

  supplierRegistrationType: z.string(),
  supplierRegistrationNumber: z.string(),
  supplierTin: z.string(),
  supplierName: z.string(),
  supplierSstRegistrationNumber: z.string().nullable(),
  supplierMsicCode: z.string(),
  supplierTourismTaxRegistrationNumber: z.string().nullable(),
  supplierBusinessActivityDescription: z.string(),
  supplierEmail: z.string(),
  supplierContact: z.string(),
  supplierAddressLine0: z.string(),
  supplierAddressLine1: z.string().nullable(),
  supplierAddressLine2: z.string().nullable(),
  supplierCity: z.string(),
  supplierPostcode: z.string(),
  supplierState: z.string(),
  supplierCountry: z.string(),
  items: z.array(lineItemOcrSchema)
});

export type SelfServiceForm = z.infer<typeof selfServiceSchema>;
export type SelfServiceBuyerForm = z.infer<typeof selfServiceBuyerDetails>;
export type SelfServiceInvoiceDetails = z.infer<
  typeof selfServiceInvoiceSchema
>;
export type SelfServiceOcrInvoiceDetails = z.infer<
  typeof selfServiceOcrInvoiceSchema
>;